import { useLazyQuery } from "@apollo/client";
import { SelectOption } from "@aurem/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { gql, Operation, Operator } from "types";

const GET_COMPANIES_LIST = gql(/* GraphQL */ `
  query GetCompaniesListLoadMore(
    $pagination: Pagination
    $filters: FiltersExpression
  ) {
    companiesList(pagination: $pagination, filters: $filters) {
      count
      list {
        id
        name
      }
    }
  }
`);

export type UseCompaniesPaginatedOptions = {
  pageSize?: number;
  companyIdFilter?: string;
};

export const useCompaniesPaginated = (
  options?: UseCompaniesPaginatedOptions,
) => {
  const { pageSize = 20, companyIdFilter } = options || {};

  const [fetchCompanies, { data, loading, error }] =
    useLazyQuery(GET_COMPANIES_LIST);

  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadCompanies = useCallback(
    (search: string, pageNum: number) => {
      const filterConditions = [];

      if (search) {
        filterConditions.push({
          field: "name",
          op: Operation.Ilike,
          values: [`%${search}%`],
        });
      }

      if (companyIdFilter) {
        filterConditions.push({
          field: "id",
          op: Operation.Eq,
          values: [companyIdFilter],
        });
      }

      const filters =
        filterConditions.length > 0
          ? { filters: filterConditions, operator: Operator.And }
          : undefined;

      void fetchCompanies({
        variables: {
          pagination: {
            offset: (pageNum - 1) * pageSize,
            limit: pageSize,
          },
          filters,
        },
      });
    },
    [fetchCompanies, pageSize, companyIdFilter],
  );

  useEffect(() => {
    loadCompanies("", 1);
  }, []);

  useEffect(() => {
    if (data?.companiesList) {
      const newOptions = (data.companiesList.list || []).map(
        (item: { id: string; name: string }) => ({
          id: item.id,
          text: item.name,
        }),
      );

      if (page === 1) {
        setCompanyOptions(newOptions);
      } else {
        setCompanyOptions((prev) => [...prev, ...newOptions]);
      }

      const totalFetched =
        (page === 1 ? 0 : companyOptions.length) + newOptions.length;
      setHasMore(totalFetched < data.companiesList.count);
      setIsLoadingMore(false);
    }
  }, [data, page, pageSize, companyIdFilter]);

  const handleSearch = useCallback(
    (search: string) => {
      if (search === searchQuery) return;
      setSearchQuery(search);
      setPage(1);
      setCompanyOptions([]);
      setHasMore(true);
      loadCompanies(search, 1);
    },
    [loadCompanies, searchQuery],
  );

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore && !isLoadingMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      setIsLoadingMore(true);
      loadCompanies(searchQuery, nextPage);
    }
  }, [loading, hasMore, isLoadingMore, page, searchQuery, loadCompanies]);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const debouncedSearch = useCallback(
    (search: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        handleSearch(search);
      }, 300);
    },
    [handleSearch],
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    companyOptions,
    loading: loading && page === 1,
    loadingMore: isLoadingMore || (loading && page > 1),
    error,
    hasMore,
    onSearch: debouncedSearch,
    onLoadMore: handleLoadMore,
  };
};
